import {
  Box,
  Typography,
  Button,
  TextField,
  InputLabel,
  CircularProgress,
  FormHelperText,
} from '@mui/material';
import { useFormik } from 'formik';
import { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import * as Yup from 'yup';

import { blueGradientButton } from '../../../assets/styles/blueGradientButton.style';
import { axiosInstance } from '../../../assets/utils/axiosInstance';
import { isRESTAPIError } from '../../../assets/utils/helpers';
import { useToastNotificationContext } from '../../../hooks/global/alert/useToastNotification';
import ErrorType, { CustomError } from '../../../interfaces/ErrorType';
import SwitchButtonComponent from '../twoFactorAuthentication/switchButtonComponent';

const EnableEmailNotification = () => {
  const [enableNotification, setEnableNotification] = useState(false);

  const { createToastNotification } = useToastNotificationContext();
  const [userEmail, setUserEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ErrorType>({
    errorType: false,
    message: '',
  });
  const intl = useIntl();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userRoleValueResponse = await axiosInstance().get('/flags');
        setUserEmail(userRoleValueResponse.data.email);
        setEnableNotification(
          userRoleValueResponse.data.is_email_notification_enabled,
        );
      } catch (err) {
        if (isRESTAPIError(err)) {
          setError({
            errorType: true,
            message: err.message,
          });
        } else {
          setError({
            errorType: false,
            message: intl.formatMessage({
              defaultMessage: 'An unexpected error occurred.',
            }),
          });
        }
      }
    };

    fetchData();
  }, [intl]);

  // UI delay for toggle
  const SetTimeout = (
    errorMessage: string,
    newState: boolean,
    timeout: number,
  ): void => {
    setTimeout(() => {
      setError({
        errorType: true,
        message: errorMessage,
      });
      setEnableNotification(newState);
    }, timeout);
  };

  // handle onchange for toggle
  const handle2FaToggle = async (newState: boolean) => {
    setEnableNotification(newState);
    if (!userEmail) {
      return SetTimeout(
        intl.formatMessage({ defaultMessage: 'User does not have an email' }),
        !newState,
        500,
      );
    }

    try {
      const body = { is_email_notification_enabled: newState };
      await axiosInstance().patch('/flags', body);
      setError({
        errorType: false,
        message: '',
      });
      setError({
        errorType: false,
        message: '',
      });
      createToastNotification(
        `${newState
          ? intl.formatMessage({
            defaultMessage: 'Email Notification Enabled',
          })
          : intl.formatMessage({
            defaultMessage: 'Email Notification Disabled',
          })
        }`,
        'success',
      );
      return setEnableNotification(newState);
    } catch (err) {
      const customError = err as CustomError;
      const errorMessage =
        customError?.response?.data?.message ||
        customError?.response?.data?.detail ||
        intl.formatMessage({
          defaultMessage: 'Something went wrong, please try again.',
        });
      SetTimeout(errorMessage, false, 200);
      setError({
        errorType: true,
        message: errorMessage,
      });
      throw new Error(errorMessage);
    }
  };

  const formik = useFormik({
    initialValues: {
      email: '',
      submit: null,
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email(
          intl.formatMessage({
            defaultMessage: 'Please enter a valid email address.',
          }),
        )
        .required(intl.formatMessage({ defaultMessage: 'Email is required.' })),
    }),
    onSubmit: async (values, helpers) => {
      setIsLoading(true);
      try {
        const body = { email: values.email };
        const userRoleValueResponse = await axiosInstance().patch(
          '/flags',
          body,
        );
        const emailData = userRoleValueResponse.data.email;
        setUserEmail(emailData);
        createToastNotification(
          intl.formatMessage({
            defaultMessage: 'Email address successfully added.',
          }),
          'success',
        );
        formik.setValues({
          email: '',
          submit: null,
        });
        formik.setTouched({});
        formik.setErrors({});
      } catch (err) {
        helpers.setStatus({ success: false });
        const customError = err as CustomError;
        const errorMessage =
          customError?.response?.data?.email ||
          customError?.response?.data?.message ||
          intl.formatMessage({
            defaultMessage: 'Something went wrong, please try again.',
          });
        helpers.setErrors({ submit: errorMessage });
        helpers.setSubmitting(false);
      } finally {
        setIsLoading(false);
      }
    },
  });

  return (
    <Box
      sx={{
        width: '560px',
        height: 'auto',
        maxHeight: 'auto',
        borderRadius: '8px',
        border: '1px solid rgba(0, 0, 0, 0.16)',
        bgcolor: '#FFF',
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.04)',
        paddingLeft: '24px',
        paddingRight: '24px',
        paddingTop: '24px',
        paddingBottom: '36px',
      }}
    >
      <Typography
        sx={{
          color: '#000',
          fontFamily: 'Schibsted Grotesk',
          fontSize: '18px',
          fontStyle: 'normal',
          fontWeight: 500,
          lineHeight: '125%',
        }}
      >
        <FormattedMessage defaultMessage='Email Notification' />
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          mt: '20px',
        }}
      >
        <SwitchButtonComponent
          toggleSwitchState={enableNotification}
          onToggle={handle2FaToggle}
        />
        <Box style={{ width: '402px', height: '48px' }}>
          <Typography
            sx={{
              color: 'var(--grey-white-background, #878787)',
              fontFamily: 'Schibsted Grotesk',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '24px',
              marginRight: '10px',
            }}
          >
            <FormattedMessage defaultMessage='Receive email notification when your report is updated' />
          </Typography>
        </Box>
      </Box>

      {/* Error Helper */}
      <FormHelperText sx={{ color: 'red', paddingLeft: '10px' }}>
        {error.errorType ? error.message : null}
      </FormHelperText>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          mt: '30px',
        }}
      >
        <Typography
          style={{
            color: 'var(--grey-white-background, #878787)',
            fontFamily: 'Schibsted Grotesk',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: 'normal',
          }}
        >
          <FormattedMessage
            defaultMessage='If not yet registered, kindly add your email address to receive
          notifications'
          />
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          mt: '10px',
        }}
      >
        <form noValidate onSubmit={formik.handleSubmit}>
          <InputLabel
            htmlFor='user-email'
            sx={{
              color: '#000',
              fontFamily: 'Schibsted Grotesk',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: 'normal',
            }}
          >
            <FormattedMessage defaultMessage='Email Address' />
          </InputLabel>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              mt: '8px',
            }}
          >
            <TextField
              hiddenLabel
              fullWidth
              id='outlined-search'
              placeholder={intl.formatMessage({
                defaultMessage: 'Enter Email Address',
              })}
              type='email'
              error={!!(formik.touched.email && formik.errors.email)}
              value={formik.values.email}
              onChange={formik.handleChange}
              name='email'
              onBlur={formik.handleBlur}
              variant='filled'
              sx={{
                ...{
                  '& .MuiFilledInput-root:after': {
                    border: 'none',
                  },
                  '& .Mui-focused': {
                    border: 'none',
                  },
                  '& .MuiFilledInput-input': {
                    height: '20px',
                    fontWeight: 400,
                    fontFamily: 'Schibsted Grotesk',
                    fontSize: '14px',
                  },
                  '& .MuiFilledInput-root': {
                    borderRadius: '6px',
                  },
                  '& .MuiFilledInput-root:before': {
                    borderBottom: 'none',
                  },
                  pb: '0.5rem',
                  '& .MuiFilledInput-root:hover:not(.Mui-disabled,.Mui-error):before':
                  {
                    borderBottom: 'none',
                  },
                },
                ...(formik.values.email && {
                  '& .MuiFilledInput-root': {
                    backgroundColor: 'rgba(0, 0, 0, 0.01)',
                    borderRadius: '6px',
                  },
                  '& .MuiFilledInput-input': {
                    height: '19px',
                    border: '1px solid rgba(0, 0, 0, 0.50)',
                    borderRadius: '6px',
                  },
                  '& .MuiFormLabel-root': {
                    fontFamily: 'Schibsted Grotesk',
                    fontSize: '12px',
                    letterSpacing: '0em',
                    textAlign: 'left',
                    left: '.15rem',
                    color: '#000000',
                  },
                }),
              }}
            />
            <Button
              type='submit'
              disabled={isLoading}
              sx={{
                fontStyle: 'normal',
                fontWeight: 600,
                ml: 2,
                marginBottom: '7px',
                textTransform: 'none',
                ...blueGradientButton,
              }}
            >
              {isLoading ? (
                <CircularProgress
                  size={20}
                  style={{ marginRight: '8px', color: 'black' }} // Change the CircularProgress color
                />
              ) : (
                <FormattedMessage defaultMessage='Save' />
              )}
            </Button>
          </Box>
        </form>
      </Box>

      {/* Error Helper for validation */}
      <FormHelperText sx={{ color: 'red', paddingLeft: '10px' }}>
        {(formik.touched.email && formik.errors.email) || formik.errors.submit}
      </FormHelperText>
    </Box>
  );
};

export default EnableEmailNotification;
